import React, { useState, useEffect, useCallback } from "react";
import { getData } from "../../../webService/webservice.js";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";

const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const WhatsAppUsagePage = () => {
    const today = new Date();
    const [selectedYear, setSelectedYear] = useState(today.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [loading, setLoading] = useState(false);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        totalRecords: 0,
        totalPages: 1
    });

    const [summary, setSummary] = useState({
        totalStores: 0,
        totalMessagesSent: 0,
        availableStoresCount: 0,
        limitReachedStoresCount: 0
    });
    const [usageList, setUsageList] = useState([]);

    const fetchWhatsAppUsage = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("Token");
            const params = {
                year: selectedYear,
                month: selectedMonth,
                search: searchQuery,
                status: statusFilter,
                page: currentPage,
                limit: rowsPerPage
            };
            const response = await getData("whatsapp-usage", params, token);

            if (response?.status === 200 && response?.data) {
                const resData = response.data;
                if (resData.summary) {
                    setSummary(resData.summary);
                }
                if (resData.pagination) {
                    setPagination({
                        page: resData.pagination.page || 1,
                        limit: resData.pagination.limit || rowsPerPage,
                        totalRecords: resData.pagination.totalRecords || 0,
                        totalPages: resData.pagination.totalPages || 1
                    });
                }
                setUsageList(resData.data || []);
            } else {
                toast.error(response?.message || "Failed to load WhatsApp usage records");
            }
        } catch (error) {
            console.error("Error fetching WhatsApp usage:", error);
            toast.error("Error fetching WhatsApp usage details");
        } finally {
            setLoading(false);
        }
    }, [selectedYear, selectedMonth, searchQuery, statusFilter, currentPage, rowsPerPage]);

    useEffect(() => {
        fetchWhatsAppUsage();
    }, [fetchWhatsAppUsage]);

    const handleYearChange = (newYear) => {
        setSelectedYear(newYear);
        setCurrentPage(1);
    };

    const handleMonthChange = (newMonth) => {
        setSelectedMonth(newMonth);
        setCurrentPage(1);
    };

    const handleStatusChange = (newStatus) => {
        setStatusFilter(newStatus);
        setCurrentPage(1);
    };

    const handleSearchChange = (val) => {
        setSearchQuery(val);
        setCurrentPage(1);
    };

    const handleRowsPerPageChange = (newLimit) => {
        setRowsPerPage(newLimit);
        setCurrentPage(1);
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return "Never";
        try {
            const d = new Date(dateStr);
            return d.toLocaleString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true
            });
        } catch (e) {
            return String(dateStr);
        }
    };

    return (
        <div className="product-layout">
            <div style={{ padding: "24px", backgroundColor: "#f8fafc", minHeight: "100vh" }}>
                {/* Header Section */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", flexWrap: "wrap", gap: "16px" }}>
                    <div>
                        <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#0f172a", margin: "0 0 4px 0", display: "flex", alignItems: "center", gap: "10px" }}>
                            <Icon icon="mdi:whatsapp" width="28" height="28" style={{ color: "#25D366" }} />
                            Store-wise WhatsApp Quota Monitoring
                        </h2>
                        <p style={{ fontSize: "14px", color: "#64748b", margin: 0 }}>
                            Track monthly 1,500 billing message quotas and usage per store with Centralized Meta Configuration
                        </p>
                    </div>
                    <button
                        onClick={fetchWhatsAppUsage}
                        disabled={loading}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "10px 18px",
                            backgroundColor: "#3C0008",
                            color: "#fff",
                            border: "none",
                            borderRadius: "8px",
                            fontWeight: "600",
                            fontSize: "14px",
                            cursor: loading ? "not-allowed" : "pointer",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            transition: "all 0.2s"
                        }}
                    >
                        <Icon icon="mdi:refresh" width="18" height="18" className={loading ? "spin" : ""} />
                        {loading ? "Refreshing..." : "Refresh Data"}
                    </button>
                </div>

                {/* Summary Cards */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px", marginBottom: "24px" }}>
                    <div style={{ backgroundColor: "#ffffff", padding: "20px", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: "13px", fontWeight: "600", color: "#64748b", textTransform: "uppercase" }}>Total Stores</span>
                            <div style={{ width: "36px", height: "36px", borderRadius: "8px", backgroundColor: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Icon icon="mdi:store-outline" width="22" height="22" style={{ color: "#475569" }} />
                            </div>
                        </div>
                        <div style={{ fontSize: "28px", fontWeight: "700", color: "#0f172a", marginTop: "12px" }}>
                            {summary.totalStores || 0}
                        </div>
                    </div>

                    <div style={{ backgroundColor: "#ffffff", padding: "20px", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: "13px", fontWeight: "600", color: "#64748b", textTransform: "uppercase" }}>Messages Sent ({MONTH_NAMES[selectedMonth - 1]})</span>
                            <div style={{ width: "36px", height: "36px", borderRadius: "8px", backgroundColor: "#e0f2fe", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Icon icon="mdi:send-outline" width="22" height="22" style={{ color: "#0284c7" }} />
                            </div>
                        </div>
                        <div style={{ fontSize: "28px", fontWeight: "700", color: "#0284c7", marginTop: "12px" }}>
                            {(summary.totalMessagesSent || 0).toLocaleString("en-IN")}
                        </div>
                    </div>

                    <div style={{ backgroundColor: "#ffffff", padding: "20px", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: "13px", fontWeight: "600", color: "#64748b", textTransform: "uppercase" }}>Available Quota Stores</span>
                            <div style={{ width: "36px", height: "36px", borderRadius: "8px", backgroundColor: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Icon icon="mdi:check-circle-outline" width="22" height="22" style={{ color: "#16a34a" }} />
                            </div>
                        </div>
                        <div style={{ fontSize: "28px", fontWeight: "700", color: "#16a34a", marginTop: "12px" }}>
                            {summary.availableStoresCount || 0}
                        </div>
                    </div>

                    <div style={{ backgroundColor: "#ffffff", padding: "20px", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: "13px", fontWeight: "600", color: "#64748b", textTransform: "uppercase" }}>Limit Reached (1,500)</span>
                            <div style={{ width: "36px", height: "36px", borderRadius: "8px", backgroundColor: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Icon icon="mdi:alert-circle-outline" width="22" height="22" style={{ color: "#dc2626" }} />
                            </div>
                        </div>
                        <div style={{ fontSize: "28px", fontWeight: "700", color: "#dc2626", marginTop: "12px" }}>
                            {summary.limitReachedStoresCount || 0}
                        </div>
                    </div>
                </div>

                {/* Filter Bar */}
                <div style={{ backgroundColor: "#ffffff", padding: "16px 20px", borderRadius: "12px", border: "1px solid #e2e8f0", marginBottom: "24px", display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "center" }}>
                    {/* Year Filter */}
                    <div style={{ minWidth: "140px" }}>
                        <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#64748b", marginBottom: "4px" }}>Year</label>
                        <select
                            value={selectedYear}
                            onChange={(e) => handleYearChange(parseInt(e.target.value, 10))}
                            style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "14px", backgroundColor: "#fff" }}
                        >
                            {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>

                    {/* Month Filter */}
                    <div style={{ minWidth: "160px" }}>
                        <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#64748b", marginBottom: "4px" }}>Month</label>
                        <select
                            value={selectedMonth}
                            onChange={(e) => handleMonthChange(parseInt(e.target.value, 10))}
                            style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "14px", backgroundColor: "#fff" }}
                        >
                            {MONTH_NAMES.map((m, idx) => (
                                <option key={idx + 1} value={idx + 1}>{m}</option>
                            ))}
                        </select>
                    </div>

                    {/* Status Filter */}
                    <div style={{ minWidth: "160px" }}>
                        <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#64748b", marginBottom: "4px" }}>Quota Status</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => handleStatusChange(e.target.value)}
                            style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "14px", backgroundColor: "#fff" }}
                        >
                            <option value="">All Statuses</option>
                            <option value="Available">Available</option>
                            <option value="Limit Reached">Limit Reached</option>
                        </select>
                    </div>

                    {/* Search Query */}
                    <div style={{ flex: 1, minWidth: "200px" }}>
                        <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#64748b", marginBottom: "4px" }}>Search Store</label>
                        <div style={{ position: "relative" }}>
                            <input
                                type="text"
                                placeholder="Search by store name or phone..."
                                value={searchQuery}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                style={{ width: "100%", padding: "8px 12px 8px 36px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "14px" }}
                            />
                            <Icon icon="mdi:magnify" width="20" height="20" style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
                        </div>
                    </div>
                </div>

                {/* Usage Table Card */}
                <div style={{ backgroundColor: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                        <thead>
                            <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                                <th style={{ padding: "14px 20px", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase" }}>Store Name</th>
                                <th style={{ padding: "14px 20px", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase" }}>Period</th>
                                <th style={{ padding: "14px 20px", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase" }}>Monthly Limit</th>
                                <th style={{ padding: "14px 20px", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase" }}>Used Messages</th>
                                <th style={{ padding: "14px 20px", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase" }}>Remaining Messages</th>
                                <th style={{ padding: "14px 20px", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase", width: "180px" }}>Usage Progress</th>
                                <th style={{ padding: "14px 20px", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase" }}>Usage Status</th>
                                <th style={{ padding: "14px 20px", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase" }}>Last Sent At</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="8" style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
                                        <Icon icon="mdi:loading" width="28" height="28" className="spin" style={{ marginBottom: "8px" }} />
                                        <div>Loading WhatsApp usage data...</div>
                                    </td>
                                </tr>
                            ) : usageList.length === 0 ? (
                                <tr>
                                    <td colSpan="8" style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
                                        No WhatsApp usage records found for selected filters.
                                    </td>
                                </tr>
                            ) : (
                                usageList.map((item, index) => {
                                    const percentUsed = Math.min(100, Math.round(((item.usedCount || 0) / (item.monthlyLimit || 1500)) * 100));
                                    const isLimitReached = item.status === "Limit Reached" || (item.usedCount || 0) >= (item.monthlyLimit || 1500);

                                    return (
                                        <tr key={item.storeId || index} style={{ borderBottom: "1px solid #f1f5f9", backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8fafc" }}>
                                            <td style={{ padding: "14px 20px" }}>
                                                <div style={{ fontWeight: "600", color: "#0f172a", fontSize: "14px" }}>
                                                    {item.storeName}
                                                </div>
                                            </td>
                                            <td style={{ padding: "14px 20px", fontSize: "14px", color: "#475569" }}>
                                                {MONTH_NAMES[(item.month || selectedMonth) - 1]} {item.year || selectedYear}
                                            </td>
                                            <td style={{ padding: "14px 20px", fontSize: "14px", fontWeight: "600", color: "#0f172a" }}>
                                                {(item.monthlyLimit || 1500).toLocaleString("en-IN")}
                                            </td>
                                            <td style={{ padding: "14px 20px", fontSize: "14px", fontWeight: "700", color: isLimitReached ? "#dc2626" : "#0284c7" }}>
                                                {(item.usedCount || 0).toLocaleString("en-IN")}
                                            </td>
                                            <td style={{ padding: "14px 20px", fontSize: "14px", fontWeight: "600", color: (item.remainingCount || 0) === 0 ? "#dc2626" : "#16a34a" }}>
                                                {(item.remainingCount ?? 1500).toLocaleString("en-IN")}
                                            </td>
                                            <td style={{ padding: "14px 20px" }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                    <div style={{ flex: 1, height: "8px", backgroundColor: "#e2e8f0", borderRadius: "4px", overflow: "hidden" }}>
                                                        <div
                                                            style={{
                                                                width: `${percentUsed}%`,
                                                                height: "100%",
                                                                backgroundColor: isLimitReached ? "#dc2626" : percentUsed > 80 ? "#f59e0b" : "#25D366",
                                                                borderRadius: "4px",
                                                                transition: "width 0.4s ease"
                                                            }}
                                                        />
                                                    </div>
                                                    <span style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", minWidth: "36px" }}>{percentUsed}%</span>
                                                </div>
                                            </td>
                                            <td style={{ padding: "14px 20px" }}>
                                                <span style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: "4px",
                                                    padding: "4px 10px",
                                                    borderRadius: "20px",
                                                    fontSize: "12px",
                                                    fontWeight: "600",
                                                    backgroundColor: isLimitReached ? "#fee2e2" : "#dcfce7",
                                                    color: isLimitReached ? "#991b1b" : "#166534"
                                                }}>
                                                    <Icon icon={isLimitReached ? "mdi:alert-circle" : "mdi:check-circle"} width="14" height="14" />
                                                    {isLimitReached ? "Limit Reached" : "Available"}
                                                </span>
                                            </td>
                                            <td style={{ padding: "14px 20px", fontSize: "13px", color: "#64748b" }}>
                                                {formatDateTime(item.lastMessageSentAt)}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>

                    {/* Pagination Bar */}
                    <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "16px 20px",
                        backgroundColor: "#ffffff",
                        borderTop: "1px solid #e2e8f0",
                        flexWrap: "wrap",
                        gap: "16px"
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                            <span style={{ fontSize: "13px", color: "#64748b" }}>
                                Showing {pagination.totalRecords === 0 ? 0 : (currentPage - 1) * (rowsPerPage === "all" ? pagination.totalRecords : rowsPerPage) + 1} to {rowsPerPage === "all" ? pagination.totalRecords : Math.min(currentPage * rowsPerPage, pagination.totalRecords)} of {pagination.totalRecords} stores
                            </span>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span style={{ fontSize: "13px", color: "#64748b" }}>Stores per page:</span>
                                <select
                                    value={rowsPerPage}
                                    onChange={(e) => handleRowsPerPageChange(e.target.value === "all" ? "all" : parseInt(e.target.value, 10))}
                                    style={{ padding: "6px 10px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", backgroundColor: "#fff", cursor: "pointer" }}
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value="all">All</option>
                                </select>
                            </div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage <= 1 || loading}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    padding: "6px 12px",
                                    borderRadius: "6px",
                                    border: "1px solid #cbd5e1",
                                    backgroundColor: currentPage <= 1 ? "#f1f5f9" : "#fff",
                                    color: currentPage <= 1 ? "#94a3b8" : "#0f172a",
                                    cursor: currentPage <= 1 ? "not-allowed" : "pointer",
                                    fontSize: "13px",
                                    fontWeight: "600"
                                }}
                            >
                                <Icon icon="mdi:chevron-left" width="18" height="18" />
                                Previous
                            </button>

                            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                                .filter(p => p === 1 || p === pagination.totalPages || Math.abs(p - currentPage) <= 1)
                                .map((p, idx, arr) => {
                                    const prevP = arr[idx - 1];
                                    const showEllipsis = prevP && p - prevP > 1;
                                    return (
                                        <React.Fragment key={p}>
                                            {showEllipsis && <span style={{ padding: "0 4px", color: "#94a3b8" }}>...</span>}
                                            <button
                                                onClick={() => setCurrentPage(p)}
                                                style={{
                                                    padding: "6px 12px",
                                                    borderRadius: "6px",
                                                    border: p === currentPage ? "none" : "1px solid #cbd5e1",
                                                    backgroundColor: p === currentPage ? "#3C0008" : "#fff",
                                                    color: p === currentPage ? "#fff" : "#0f172a",
                                                    fontWeight: "600",
                                                    fontSize: "13px",
                                                    cursor: "pointer"
                                                }}
                                            >
                                                {p}
                                            </button>
                                        </React.Fragment>
                                    );
                                })
                            }

                            <button
                                onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                                disabled={currentPage >= pagination.totalPages || loading}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    padding: "6px 12px",
                                    borderRadius: "6px",
                                    border: "1px solid #cbd5e1",
                                    backgroundColor: currentPage >= pagination.totalPages ? "#f1f5f9" : "#fff",
                                    color: currentPage >= pagination.totalPages ? "#94a3b8" : "#0f172a",
                                    cursor: currentPage >= pagination.totalPages ? "not-allowed" : "pointer",
                                    fontSize: "13px",
                                    fontWeight: "600"
                                }}
                            >
                                Next
                                <Icon icon="mdi:chevron-right" width="18" height="18" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WhatsAppUsagePage;
